import {VOICE_CHARACTERS, VOICE_SLOTS, BUILTIN_VOICE_SLUGS} from '../../static/mod/modSchema.js';

// Built-in recordings. Mods may supply any of the sixteen characters.
export const CHARACTER_VOICES = Object.freeze(Object.fromEntries(VOICE_CHARACTERS
    .filter(character => BUILTIN_VOICE_SLUGS.includes(character.slug)).map(({skin, slug}) => [skin, slug])));

export const VOICE_TUNING = Object.freeze({
    volume: 0.65,
    minimumDamage: 6,
    damageWindow: 250,
    damageCooldown: 3500,
    damageCooldownVariation: 2000,
    voiceGap: 1600,
    lineCooldown: 10000,
    sameLineCooldown: 25000,
    swearChance: 0.25,
    swearCooldown: 15000,
    terminalCooldown: 2000,
});

// Keep repetition limits across quick retries; life/terminal state stays local.
const histories = new Map();
const freshHistory = () => ({
    voiceAt: -Infinity, damageUntil: -Infinity, lineAt: -Infinity,
    swearAt: -Infinity, terminalAt: -Infinity, lastDamage: null, lines: {},
});

export function createCharacterVoice(skin, audio) {
    const slug = VOICE_CHARACTERS[skin]?.slug;
    const available = CHARACTER_VOICES[skin] || (slug && Object.keys(VOICE_SLOTS)
        .some(slot => audio.hasCharacterVoice?.(`${slug}_${slot}`)));
    return available ? new CharacterVoiceController(slug, audio) : null;
}

export class CharacterVoiceController {
    constructor(slug, audio, {now = () => performance.now(), random = Math.random, history} = {}) {
        this.slug = slug;
        this.audio = audio;
        this.now = now;
        this.random = random;
        if (!histories.has(slug)) histories.set(slug, freshHistory());
        this.history = history || histories.get(slug);
        this.handle = null;
        this.terminal = false;
        this.disposed = false;
        this.impactAt = -Infinity;
        this.impactDamage = 0;
        if (audio.preloadCharacterVoice) audio.preloadCharacterVoice(slug);
        else audio.preloadSFX(`${slug}_damage_01`);
    }

    play(event, damage = 0) {
        if (this.disposed || this.terminal) return false;
        const now = this.now();
        const h = this.history;
        const terminal = event === 'death' || event === 'win';
        if (terminal) {
            // Death/win ends chatter even if audio is muted or still loading.
            this.terminal = true;
            this.handle?.stop();
            if (now - h.terminalAt < VOICE_TUNING.terminalCooldown) return false;
        } else if (event === 'damage') {
            if (!Number.isFinite(damage) || damage <= 0) return false;
            if (now - this.impactAt > VOICE_TUNING.damageWindow) {
                this.impactAt = now;
                this.impactDamage = 0;
            }
            this.impactDamage += damage;
            if (this.impactDamage < VOICE_TUNING.minimumDamage) return false;
            this.impactDamage = 0;
            if (now < h.damageUntil) return false;
        } else if (event === 'hat' || event === 'eject') {
            if (now - h.lineAt < VOICE_TUNING.lineCooldown ||
                now - (h.lines[event] ?? -Infinity) < VOICE_TUNING.sameLineCooldown) return false;
        } else {
            return false;
        }

        // Drop occupied moments. No delayed jokes after the event has passed.
        if (!terminal && (this.handle?.playing() || now - h.voiceAt < VOICE_TUNING.voiceGap)) return false;
        let slot = `${event}_01`;
        if (event === 'damage') {
            const available = name => !this.audio.hasCharacterVoice || this.audio.hasCharacterVoice(`${this.slug}_${name}`);
            if (h.lastDamage !== 'damage_word_01' && now - h.swearAt >= VOICE_TUNING.swearCooldown &&
                available('damage_word_01') && this.random() < VOICE_TUNING.swearChance) {
                slot = 'damage_word_01';
            } else {
                const grunts = ['damage_01', 'damage_02', 'damage_03'].filter(available);
                const alternatives = grunts.filter(name => name !== h.lastDamage);
                const choices = alternatives.length ? alternatives : grunts;
                if (!choices.length) return false;
                slot = choices[Math.floor(this.random() * choices.length)];
            }
        }
        const handle = this.audio.playCharacterVoice(`${this.slug}_${slot}`, VOICE_TUNING.volume, terminal);
        if (!handle) return false;
        this.handle = handle;
        h.voiceAt = now;
        if (terminal) h.terminalAt = now;
        if (event === 'damage') {
            h.lastDamage = slot;
            h.damageUntil = now + VOICE_TUNING.damageCooldown + this.random() * VOICE_TUNING.damageCooldownVariation;
            if (slot === 'damage_word_01') h.swearAt = now;
        }
        if (event === 'hat' || event === 'eject') {
            h.lineAt = now;
            h.lines[event] = now;
        }
        return true;
    }

    dispose() {
        this.disposed = true;
        this.handle?.stop();
        this.handle = null;
    }
}
